// src/features/terminal/useTerminalStream.js
import { useEffect, useRef, useState, useCallback } from 'react';
import { getAuthToken } from '@/lib/trackizy';

const WS_PATH = '/api/socket';

export function useTerminalStream(filter, { bufferMax = 500, paused = false } = {}) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('idle');
  const [subscriptionId, setSubscriptionId] = useState(null);
  const wsRef = useRef(null);
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const connect = useCallback(() => {
    const token = getAuthToken();
    if (!token || !filterRef.current) return;
    const url = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}${WS_PATH}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    setStatus('connecting');

    ws.addEventListener('open', () => {
      setStatus('open');
      ws.send(JSON.stringify({ op: 'terminal.subscribe', filter: filterRef.current }));
    });
    ws.addEventListener('message', (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === 'terminal.subscribed') { setSubscriptionId(msg.subscriptionId); return; }
      if (msg.type === 'terminal.error')      { setStatus('error'); return; }
      if (msg.type === 'terminal.row' && !pausedRef.current) {
        setRows(prev => {
          const next = [msg.row, ...prev];
          return next.length > bufferMax ? next.slice(0, bufferMax) : next;
        });
      }
    });
    ws.addEventListener('close', () => setStatus('closed'));
    ws.addEventListener('error', () => setStatus('error'));
  }, [bufferMax]);

  useEffect(() => {
    if (!filter) return;
    connect();
    return () => { try { wsRef.current?.close(); } catch {}; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const updateFilter = useCallback((newFilter) => {
    filterRef.current = newFilter;
    if (wsRef.current && wsRef.current.readyState === 1 && subscriptionId) {
      wsRef.current.send(JSON.stringify({ op: 'terminal.updateFilter', subscriptionId, filter: newFilter }));
      setRows([]);
    }
  }, [subscriptionId]);

  const clear = useCallback(() => setRows([]), []);

  return { rows, status, subscriptionId, updateFilter, clear };
}
